/** @jsxImportSource @emotion/react */
import { Table, TableProps, Toggle, TokenIconWithSymbol, Icon } from 'components';
import React, { useMemo, useContext } from 'react';
import { useTranslation } from 'translation';
import { Asset, Vault } from 'types';
import { formatToReadablePercentage, formatTokensToReadableValue } from 'utilities';
import { css } from '@emotion/react';
import BigNumber from 'bignumber.js';

import PLACEHOLDER_KEY from 'constants/placeholderKey';
import { usePreviewStakeRewards, useGetGlobalStakingStats } from 'clients/api';
import { AuthContext } from 'context/AuthContext';

import { useStyles as useSharedStyles } from '../styles';
import { useStyles } from './styles';

export interface SupplyMarketTableUiProps {
  assets: Asset[];
  vaults: Vault[];
  isXvsEnabled: boolean;
  rowOnClick: (e: React.MouseEvent<HTMLElement>, row: TableProps['data'][number]) => void;
  collateralOnChange: (asset: Vault) => void;
}

export const SupplyMarketTable: React.FC<SupplyMarketTableUiProps> = ({
  assets,
  vaults,
  isXvsEnabled,
  collateralOnChange,
  rowOnClick,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const sharedStyles = useSharedStyles();
  const { account } = useContext(AuthContext);

  // 预览质押奖励 - 以第一个 vault 为例，质押 1000 USDT
  const { data: previewData } = usePreviewStakeRewards(
    {
      accountAddress: account?.address || '',
      amount: new BigNumber('1000'),
    },
    {
      enabled: !!account?.address,
    },
  );

  // 获取全局质押统计数据
  const { data: globalStats } = useGetGlobalStakingStats(
    {
      accountAddress: account?.address,
    },
    {
      enabled: true,
    },
  );

  // 质押池标题样式
  const stakingTitleStyles = css`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    padding: 12px 16px;
    background: rgba(55, 65, 81, 0.6);
    border-radius: 8px;
    border: 1px solid rgba(75, 85, 99, 0.3);
    
    .staking-icon {
      color: #10b981;
      font-size: 18px;
    }
    
    .staking-title {
      color: #e5e7eb;
      font-size: 15px;
      font-weight: 500;
      margin: 0;
    }
    
    .staking-count {
      color: #9ca3af;
      font-size: 13px;
      margin-left: auto;
    }
  `;

  const columns = useMemo(
    () => [
      { key: 'asset', label: t('markets.columns.asset'), orderable: false, align: 'left' },
      { key: 'apy', label: '预估月化', orderable: true, align: 'right' },
      { key: 'wallet', label: '日收益', orderable: true, align: 'right' },
      {
        key: 'collateral',
        label: '月收益',
        orderable: true,
        align: 'right',
      },
    ],
    [],
  );

  // Format assets to rows
  const rows: TableProps['data'] = vaults.map((asset, index) => {
    const supplyApy = isXvsEnabled ? asset.xvsSupplyApy.plus(asset.supplyApy) : asset.supplyApy;

    // 计算显示值
    const getMonthlyApy = () => {
      if (globalStats) return `${globalStats.averageApy.dividedBy(12).toFixed(2)}%`;
      return formatToReadablePercentage(supplyApy);
    };

    const getDailyReward = () => {
      if (globalStats) return `${globalStats.dailyReward.dividedBy(new BigNumber(10).pow(18)).toFixed(4)}`;
      return '0.0000';
    };

    const getMonthlyReward = () => {
      if (globalStats) return `${globalStats.monthlyReward.dividedBy(new BigNumber(10).pow(18)).toFixed(2)}`;
      return '0.00';
    };

    // 计算排序值
    const getApyValue = () => {
      if (globalStats) return globalStats.averageApy.dividedBy(12).toNumber();
      return supplyApy.toNumber();
    };

    const getDailyRewardValue = () => {
      if (globalStats) return globalStats.dailyReward.dividedBy(new BigNumber(10).pow(18)).toFixed();
      return asset.walletBalance.toFixed();
    };

    const getMonthlyRewardValue = () => {
      if (globalStats) return globalStats.monthlyReward.dividedBy(new BigNumber(10).pow(18)).toNumber();
      return 0;
    };

    return [
      {
        key: 'asset',
        render: () => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon
              name="vault"
              css={css`
                color: #10b981;
                font-size: 16px;
              `}
            />
            <TokenIconWithSymbol token={asset.rewardTokenId} /> + <TokenIconWithSymbol token={asset.stakedTokenId} />
          </div>
        ),
        value: asset.id,
        align: 'left',
      },
      {
        key: 'apy',
        render: () => (
          <div style={{ textAlign: 'left' }}>
            <div style={{ color: '#10b981', fontWeight: 600 }}>
              {getMonthlyApy()}
            </div>
          </div>
        ),
        value: getApyValue(),
        align: 'right',
      },
      {
        key: 'wallet',
        render: () => (
          <div style={{ textAlign: 'left' }}>
            <div style={{ color: '#3b82f6', fontWeight: 600 }}>
              {getDailyReward()}
            </div>
          </div>
        ),
        value: getDailyRewardValue(),
        align: 'right',
      },
      {
        key: 'collateral',
        render: () => (
          <div style={{ textAlign: 'left' }}>
            <div style={{ color: '#f59e0b', fontWeight: 600 }}>
              {getMonthlyReward()}
            </div>
          </div>
        ),
        value: getMonthlyRewardValue(),
        align: 'right',
      },
    ];
  });

  return (
    <>
      {/* 质押池标题指示器 */}
      <div css={stakingTitleStyles}>
        <Icon name="vault" className="staking-icon" />
        <span className="staking-title">质押池</span>
        <span className="staking-count">{vaults.length} 个活跃池</span>
      </div>
      <Table
        // title={t('markets.supplyMarketTableTitle')}
        columns={columns}
        data={rows}
        initialOrder={{
          orderBy: 'apy',
          orderDirection: 'desc',
        }}
        rowOnClick={rowOnClick}
        rowKeyIndex={0}
        tableCss={sharedStyles.table}
        cardsCss={sharedStyles.cards}
        css={[sharedStyles.marketTable, sharedStyles.generalMarketTable, styles.cardContentGrid]}
      />
    </>
  );
};

export default SupplyMarketTable;
